// Refactored DocumentExceptionDetails modal version using MaterializeCSS collapsibles (multiple open enabled)
'use client';
import M from 'materialize-css';
import { useState, useMemo, useRef, useLayoutEffect } from 'react';
import { useParams } from 'react-router-dom';
import styled from '@emotion/styled';
import queryHooks from '../../common/queryHooks/index.ts';

const List = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  padding: 1.25rem;
`;

const LineItem = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  border-bottom: 1px dashed rgba(0, 0, 0, 0.3);
  padding: 0.5rem;
  font-size: 0.9rem;
  &:hover {
    background-color: rgba(217, 217, 217, 0.4);
  }
`;

const LineItemLabel = styled.span`
  text-align: left;
  margin-right: 1rem;
  min-width: 20%;
  font-weight: 500;
`;

const titles = {
  header: {
    label: 'Header',
    keyMap: {
      'Date Created': 'dateCreated',
      'Doc Type': 'docType',
      'Header Version': 'headerVersion',
      Identifier: 'identifier',
      'Receiver SGLN': 'receiverSgln',
    },
  },
  events: {
    label: 'Events',
    keyMap: {
      Action: 'action',
      'Biz Step': 'bizStep',
      'Biz Transaction List': 'bizTransactionList',
      Disposition: 'disposition',
      'EPC List': 'epcList',
      'Child EPCs': 'childEPCs',
      'Event Time': 'eventTime',
      'Event Timezone Offset': 'eventTimezoneOffset',
      Extension: 'extension',
      'Read Point': 'readPoint',
      Type: 'type',
    },
  },
  'Biz Transaction List': {
    keyMap: { Type: 'type', Identifier: 'identifier' },
  },
  Extension: {
    keyMap: {
      Source: 'source',
      Destination: 'destination',
      'Lot Number': 'lotNumber',
      'Expiration Date': 'expirationDate',
    },
  },
  Destination: {
    keyMap: {
      Type: 'type',
      SGLN: 'sgln',
      'Lot Number': 'lotNumber',
      'Expiration Date': 'expirationDate',
    },
  },
  Source: {
    keyMap: {
      'Lot Number': 'lotNumber',
      'Expiration Date': 'expirationDate',
      Type: 'type',
      SGLN: 'sgln',
    },
  },
  locationVocabulary: {
    label: 'Location Vocabulary',
    keyMap: {
      Name: 'name',
      SGLN: 'sgln',
      'Street Address One': 'streetAddressOne',
      City: 'city',
      State: 'state',
      'Country Code': 'countryCode',
      'Postal Code': 'postalCode',
    },
  },
  meds: {
    label: 'Meds',
    keyMap: { SGTIN: 'sgtin', 'Parent SGTIN': 'parentSgtin' },
  },
  medVocabulary: {
    label: 'Med Vocabulary',
    keyMap: {
      'Additional Trade Item Identification':
        'additionalTradeItemIdentification',
      'Additional Trade Item Identification Type Code':
        'additionalTradeItemIdentificationTypeCode',
      'Dosage Form Type': 'dosageFormType',
      'Manufacturer Of Trade Item Party Name':
        'manufacturerOfTradeItemPartyName',
      'Net Content Description': 'netContentDescription',
      'Regulated Product Name': 'regulatedProductName',
      SGTIN: 'sgtin',
      'Strength Description': 'strengthDescription',
    },
  },
};

const DocumentExceptionDetails = () => {
  const { documentExceptionId } = useParams();
  const modalRef = useRef(null);
  const [modalHistory, setModalHistory] = useState([]);
  const [displayModalData, setDisplayModalData] = useState(null);

  const { data: documentExceptionErrors = null, isLoading } =
    queryHooks.documentException.useGetDocumentExceptionErrors({
      documentExceptionId: Number(documentExceptionId),
      staleTime: 5 * 60 * 1000,
    });

  useLayoutEffect(() => {
    const modal = M.Modal.init(modalRef.current);
    const collapsibles = document.querySelectorAll('.collapsible');
    M.Collapsible.init(collapsibles, { accordion: false });
    return () => modal?.destroy();
  }, [documentExceptionErrors]);

  const openModal = (title, nestedData) => {
    setModalHistory(prev =>
      displayModalData ? [...prev, displayModalData] : prev
    );
    setDisplayModalData({ title, nestedData, titleKey: title });
    const modal = M.Modal.getInstance(modalRef.current);
    modal?.open();
  };

  const goBackInModal = () => {
    if (modalHistory.length > 0) {
      const prev = [...modalHistory];
      const last = prev.pop();
      setModalHistory(prev);
      setDisplayModalData(last);
    }
  };

  const checkForNesting = (label, object, value) => {
    if (!Array.isArray(value)) {
      if (typeof value === 'object' && value !== null) {
        return (
          <span
            style={{
              cursor: 'pointer',
              textDecoration: 'underline',
              color: 'darkblue',
            }}
            onClick={() => openModal(label, value)}
          >
            View
          </span>
        );
      }
      return value || '-';
    }
    if (value.every(item => typeof item === 'object')) {
      return (
        <span
          style={{
            cursor: 'pointer',
            textDecoration: 'underline',
            color: 'darkblue',
          }}
          onClick={() => openModal(label, value)}
        >
          View
        </span>
      );
    }
    return value.join(', ');
  };

  const renderTable = (titleObj, items) => {
    const keyMap = titleObj?.keyMap ?? {};
    const headers = Object.keys(keyMap).length
      ? Object.keys(keyMap)
      : Object.keys(items[0]);
    return (
      <table>
        <thead>
          <tr>
            {headers.map((k, index) => (
              <th key={index}>{k}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((item, rowIndex) => (
            <tr key={rowIndex}>
              {headers.map((label, colIndex) => {
                const actualKey = keyMap[label] ?? label;
                const value = item[actualKey];
                return (
                  <td key={colIndex}>{checkForNesting(label, item, value)}</td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const renderList = (titleObj, item) => {
    if (!item) return null;
    const keyMap = titleObj?.keyMap ?? {};
    const headers = Object.keys(keyMap).length
      ? Object.keys(keyMap)
      : Object.keys(item);
    return (
      <List>
        {headers.map((label, index) => {
          const actualKey = keyMap[label] ?? label;
          const value = item[actualKey];
          return (
            <LineItem key={index}>
              <LineItemLabel>{label}</LineItemLabel>
              <span>{checkForNesting(label, item, value)}</span>
            </LineItem>
          );
        })}
      </List>
    );
  };

  const displayErrorOrData = (key, section) => {
    const titleObj = titles[key];
    if (section?.errors?.length || section?.items?.length || section?.data) {
      return (
        <li key={key}>
          <div className="collapsible-header">{titleObj?.label ?? key}</div>
          <div className="collapsible-body">
            {section.errors?.length ? (
              <List>
                {section.errors.map((error, i) => (
                  <LineItem key={i}>
                    <LineItemLabel>{error.name}</LineItemLabel>
                    <span>{error.description}</span>
                  </LineItem>
                ))}
              </List>
            ) : null}
            {section.items?.length
              ? renderTable(titleObj, section.items)
              : null}
            {section.data ? renderList(titleObj, section.data) : null}
          </div>
        </li>
      );
    }
    return null;
  };

  const collapsibleContent = useMemo(() => {
    const entries = Object.entries(documentExceptionErrors?.details || {});
    const sorted = entries.sort(
      ([a], [b]) =>
        Object.keys(titles).indexOf(a) - Object.keys(titles).indexOf(b)
    );
    return (
      <ul className="collapsible expandable">
        {sorted.map(([key, section]) => displayErrorOrData(key, section))}
      </ul>
    );
  }, [documentExceptionErrors]);

  const modalContent = useMemo(() => {
    if (!displayModalData) return null;
    const titleObj = titles[displayModalData.titleKey.trim()];
    const data = displayModalData.nestedData;
    return (
      <>
        {modalHistory.length > 0 && (
          <div style={{ marginBottom: '1rem' }}>
            <button
              onClick={goBackInModal}
              className="btn-flat blue-text text-darken-2"
            >
              ← Back
            </button>
          </div>
        )}
        {Array.isArray(data)
          ? renderTable(titleObj, data)
          : renderList(titleObj, data)}
      </>
    );
  }, [displayModalData, modalHistory]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        overflow: 'auto',
      }}
    >
      <div className="col" style={{ width: '100%' }}>
        {isLoading ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              minHeight: '500px',
              alignItems: 'center',
              flexDirection: 'column',
            }}
          >
            <div className="preloader-wrapper big active">
              <div className="spinner-layer spinner-blue-only">
                <div className="circle-clipper left">
                  <div className="circle"></div>
                </div>
                <div className="gap-patch">
                  <div className="circle"></div>
                </div>
                <div className="circle-clipper right">
                  <div className="circle"></div>
                </div>
              </div>
            </div>
            <span>Fetching Document Data . . .</span>
          </div>
        ) : (
          collapsibleContent
        )}
      </div>
      <div ref={modalRef} id="report-edit" className="modal">
        <div className="modal-content">
          <div className="row">
            <h5 className="bolder" style={{ textAlign: 'center' }}>
              <i>{displayModalData?.title}</i>
            </h5>
          </div>
          {modalContent}
        </div>
      </div>
    </div>
  );
};

export default DocumentExceptionDetails;
